import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Megaphone, Plus, Trash, PaperPlaneTilt, Eye, Paperclip, FlyingSaucer, ChartLine, X,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BODY = `<p>Gentile <strong>{{nome_cliente}}</strong>,</p>

<p>abbiamo il piacere di inviarti questa comunicazione da parte di Orange Energy.</p>

<p>[Inserisci qui il contenuto della comunicazione]</p>

<p>Rimaniamo a tua disposizione per qualsiasi informazione.</p>
`;

export default function MarketingCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [groups, setGroups] = useState([]);
  const [documents, setDocuments] = useState({ system: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState(initForm());
  const [testEmail, setTestEmail] = useState('');
  const [uploading, setUploading] = useState(false);

  function initForm() {
    return {
      name: '', subject: '', html_body: DEFAULT_BODY,
      group_ids: [], attachments_system: [], attachments_uploaded: [],
      respect_consent: true,
    };
  }

  const fetchData = useCallback(async () => {
    try {
      const [cRes, gRes, dRes] = await Promise.all([
        fetch(`${API}/api/marketing/campaigns`, { credentials: 'include' }),
        fetch(`${API}/api/marketing/groups`, { credentials: 'include' }),
        fetch(`${API}/api/documents`, { credentials: 'include' }),
      ]);
      if (cRes.ok) setCampaigns(await cRes.json());
      if (gRes.ok) setGroups(await gRes.json());
      if (dRes.ok) setDocuments(await dRes.json());
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveCampaign = async () => {
    if (!form.name.trim() || !form.subject.trim()) {
      return toast.error('Nome e oggetto sono obbligatori');
    }
    try {
      const isNew = editing === 'new';
      const url = isNew ? `${API}/api/marketing/campaigns`
                        : `${API}/api/marketing/campaigns/${editing.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isNew ? 'Campagna creata' : 'Aggiornata');
        if (isNew && data.id) {
          setEditing({ ...form, id: data.id });
        }
        fetchData();
      } else toast.error(data.detail || 'Errore salvataggio');
    } catch (e) { toast.error('Errore'); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Eliminare campagna?')) return;
    try {
      const res = await fetch(`${API}/api/marketing/campaigns/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Eliminata'); fetchData(); }
    } catch (e) { toast.error('Errore'); }
  };

  const sendTest = async () => {
    if (!editing || editing === 'new') return toast.error('Salva prima la campagna');
    if (!testEmail) return toast.error('Inserisci email di test');
    try {
      const res = await fetch(`${API}/api/marketing/campaigns/${editing.id}/test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_email: testEmail }),
      });
      const data = await res.json();
      if (res.ok) toast.success(`Test inviato a ${testEmail}`);
      else toast.error(data.detail || 'Errore invio test');
    } catch (e) { toast.error('Errore'); }
  };

  const sendCampaign = async (id) => {
    if (!window.confirm('Avviare invio campagna? L\'azione è irreversibile.')) return;
    try {
      const res = await fetch(`${API}/api/marketing/campaigns/${id}/send`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) { toast.success('Invio campagna avviato in background'); fetchData(); }
      else toast.error(data.detail || 'Errore');
    } catch (e) { toast.error('Errore'); }
  };

  const uploadAttachment = async (file) => {
    if (!editing || editing === 'new') return toast.error('Salva prima la campagna');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/marketing/campaigns/${editing.id}/upload-attachment`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Allegato caricato');
        setForm({ ...form, attachments_uploaded: [...(form.attachments_uploaded || []), data] });
      } else toast.error(data.detail || 'Errore upload');
    } catch (e) { toast.error('Errore upload'); }
    finally { setUploading(false); }
  };

  const deleteAttachment = async (filename) => {
    if (!editing || editing === 'new') return;
    try {
      await fetch(`${API}/api/marketing/campaigns/${editing.id}/attachments/${encodeURIComponent(filename)}`, {
        method: 'DELETE', credentials: 'include',
      });
      setForm({ ...form, attachments_uploaded: (form.attachments_uploaded || []).filter(a => a.filename !== filename) });
      toast.success('Allegato rimosso');
    } catch (e) { toast.error('Errore'); }
  };

  const openReport = async (id) => {
    try {
      const res = await fetch(`${API}/api/marketing/campaigns/${id}/report`, { credentials: 'include' });
      if (res.ok) setReport(await res.json());
    } catch (e) { toast.error('Errore'); }
  };

  const openNew = () => { setForm(initForm()); setEditing('new'); };
  const openEdit = (c) => { setForm({
    name: c.name || '', subject: c.subject || '', html_body: c.html_body || DEFAULT_BODY,
    group_ids: c.group_ids || [], attachments_system: c.attachments_system || [],
    attachments_uploaded: c.attachments_uploaded || [], respect_consent: c.respect_consent ?? true,
  }); setEditing(c); };

  const toggleGroup = (gid) => {
    const arr = form.group_ids || [];
    const next = arr.includes(gid) ? arr.filter(g => g !== gid) : [...arr, gid];
    setForm({ ...form, group_ids: next });
  };

  const toggleSystemDoc = (filename) => {
    const arr = form.attachments_system || [];
    const next = arr.includes(filename) ? arr.filter(a => a !== filename) : [...arr, filename];
    setForm({ ...form, attachments_system: next });
  };

  const systemDocs = [...(documents.system || []), ...(documents.custom || [])];

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="marketing-campaigns-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Megaphone size={32} weight="duotone" className="text-orange-500" />
            Campagne Marketing
          </h1>
          <p className="text-slate-500 mt-1">Crea e invia campagne email ai tuoi gruppi.</p>
        </div>
        <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600" data-testid="create-campaign-btn">
          <Plus size={18} className="mr-2" /> Nuova campagna
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <Megaphone size={64} className="mx-auto text-slate-300 mb-4" weight="duotone" />
          <p className="text-slate-500">Nessuna campagna. Crea la prima ora.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-slate-900">{c.name}</h3>
                    <Badge variant={c.status === 'sent' ? 'default' : c.status === 'sending' ? 'secondary' : 'outline'}
                           className={c.status === 'sent' ? 'bg-green-100 text-green-700' : ''}>
                      {c.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{c.subject}</p>
                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                    <span>Gruppi: {c.group_ids?.length || 0}</span>
                    <span>Totale: {c.stats?.total || 0}</span>
                    <span className="text-green-700">Inviate: {c.stats?.sent || 0}</span>
                    <span className="text-blue-700">Aperte: {c.stats?.opened || 0}</span>
                    <span className="text-red-600">Errori: {c.stats?.failed || 0}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openReport(c.id)} title="Report">
                    <ChartLine size={16} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)}>
                    <Eye size={16} />
                  </Button>
                  {c.status === 'draft' && (
                    <Button size="sm" className="bg-orange-500 hover:bg-orange-600"
                            onClick={() => sendCampaign(c.id)} data-testid={`send-campaign-${c.id}`}>
                      <PaperPlaneTilt size={16} className="mr-1" /> Invia
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => deleteCampaign(c.id)} className="text-red-600">
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EDITOR DIALOG */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nuova campagna' : 'Modifica campagna'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome campagna</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="campaign-name-input" />
              </div>
              <div>
                <Label>Oggetto email</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                       data-testid="campaign-subject-input" />
              </div>
            </div>

            <div>
              <Label>Corpo email (HTML) &mdash; variabili: <code className="text-xs">{'{{nome_cliente}}'}</code></Label>
              <Textarea value={form.html_body} onChange={(e) => setForm({ ...form, html_body: e.target.value })}
                        rows={12} className="font-mono text-xs" data-testid="campaign-body-input" />
            </div>

            <div className="border rounded-lg p-3 bg-slate-50">
              <Label className="mb-2 block">Gruppi destinatari ({form.group_ids?.length || 0})</Label>
              <div className="flex flex-wrap gap-2">
                {groups.length === 0 && <p className="text-sm text-slate-500">Nessun gruppo. Creane uno in "Gruppi".</p>}
                {groups.map(g => (
                  <Badge key={g.id}
                         variant={form.group_ids?.includes(g.id) ? 'default' : 'outline'}
                         onClick={() => toggleGroup(g.id)}
                         className="cursor-pointer">
                    {g.name} ({g.email_count})
                  </Badge>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm mt-3">
                <Checkbox checked={form.respect_consent}
                          onCheckedChange={(v) => setForm({ ...form, respect_consent: !!v })} />
                Rispetta consenso marketing (salta leads senza consenso)
              </label>
            </div>

            <div className="border rounded-lg p-3">
              <Label className="mb-2 block"><Paperclip size={14} className="inline mr-1" /> Allegati</Label>
              <div className="text-xs text-slate-500 mb-2">Documenti di sistema</div>
              <div className="flex flex-wrap gap-2">
                {systemDocs.map(d => (
                  <Badge key={d.id}
                         variant={form.attachments_system?.includes(d.id) ? 'default' : 'outline'}
                         onClick={() => toggleSystemDoc(d.id)}
                         className="cursor-pointer">{d.label || d.filename}</Badge>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-3 mb-2">Upload nuovo allegato (per questa campagna)</div>
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg"
                     onChange={(e) => e.target.files[0] && uploadAttachment(e.target.files[0])}
                     className="text-sm" disabled={uploading || editing === 'new'} />
              {editing === 'new' && <p className="text-xs text-orange-600 mt-1">Salva prima la campagna per caricare allegati</p>}
              {form.attachments_uploaded?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {form.attachments_uploaded.map((a, i) => (
                    <li key={i} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded">
                      <span>{a.filename} <span className="text-xs text-slate-400">({Math.round(a.size / 1024)} KB)</span></span>
                      <button onClick={() => deleteAttachment(a.filename)} className="text-red-600"><X size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {editing !== 'new' && (
              <div className="border rounded-lg p-3 bg-blue-50/50">
                <Label className="mb-1 block">Invia email di test</Label>
                <div className="flex gap-2">
                  <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                         placeholder="tua@email.it" type="email" />
                  <Button onClick={sendTest} variant="outline"><FlyingSaucer size={16} className="mr-1" /> Test</Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Chiudi</Button>
            <Button onClick={saveCampaign} className="bg-orange-500 hover:bg-orange-600" data-testid="save-campaign-btn">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPORT DIALOG */}
      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Report: {report?.campaign?.name}</DialogTitle></DialogHeader>
          {report && (
            <div>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-slate-50 p-3 rounded text-center"><div className="text-2xl font-bold">{report.stats.total}</div><div className="text-xs text-slate-500">Totale</div></div>
                <div className="bg-green-50 p-3 rounded text-center"><div className="text-2xl font-bold text-green-700">{report.stats.sent}</div><div className="text-xs text-slate-500">Consegnate</div></div>
                <div className="bg-blue-50 p-3 rounded text-center"><div className="text-2xl font-bold text-blue-700">{report.stats.opened}</div><div className="text-xs text-slate-500">Aperte ({report.stats.open_rate}%)</div></div>
                <div className="bg-red-50 p-3 rounded text-center"><div className="text-2xl font-bold text-red-700">{report.stats.failed}</div><div className="text-xs text-slate-500">Errori</div></div>
              </div>
              <div className="border rounded max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Destinatario</th>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">Stato</th>
                      <th className="text-left p-2">Inviata</th>
                      <th className="text-left p-2">Aperta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.communications?.map(c => (
                      <tr key={c.id} className="border-t">
                        <td className="p-2">{c.ragione_sociale || '—'}</td>
                        <td className="p-2">{c.to_email}</td>
                        <td className="p-2"><Badge variant="outline" className={
                          c.status === 'opened' ? 'bg-blue-50 text-blue-700' :
                          c.status === 'sent' ? 'bg-green-50 text-green-700' :
                          c.status === 'failed' ? 'bg-red-50 text-red-700' : ''}>{c.status}</Badge></td>
                        <td className="p-2 text-xs">{c.sent_at ? new Date(c.sent_at).toLocaleString('it-IT') : '—'}</td>
                        <td className="p-2 text-xs">{c.opened_at ? new Date(c.opened_at).toLocaleString('it-IT') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
